import SHARED_ICON_NODES from 'lucide-nodes:arrow-up-right'
import type { Map as MapboxMap } from 'mapbox-gl'

import {
  MAP_SHADOW_LAYERS,
  MAP_SHADOW_REACH_PX,
} from '~/client/src/shared/map/markerGeometry'

import {
  DEFAULT_GLYPH_STROKE_WIDTH,
  iconNodesToPath2Ds,
  traceRoundRect,
} from './iconGeometry'

import Colors from '~/client/src/shared/theme.module.scss'

export const SHARED_CHIP_IMAGE_ID = 'shared-chip'

// A rounded grey plate, a white outline, the arrow glyph and the word beside it. Its grey is
// the pin's own — the chip hangs off a shared pin and the two read as one object, so it
// follows the fill a shared pin resolves to rather than keeping a grey of its own. Literal,
// not get_color: a canvas cannot read a CSS variable.
const ICON_PX = 14
// Tighter on the leading side, where the glyph already stands off the edge.
const PAD_LEFT_PX = 2
const PAD_RIGHT_PX = 4
const BORDER_PX = 1.5
const RADIUS_PX = 6
// The text's line box. The plate takes its height from this and the outline, not from the
// glyph, which is shorter and rides centred in it.
const LINE_HEIGHT_PX = 16
const HEIGHT_PX = LINE_HEIGHT_PX + BORDER_PX * 2
// The one stretching slice. A pixel is the floor: rounded to zero at a fractional dpr the
// stretch total is zero and the fit divides by it.
const BAND_PX = 2
const ICON_GRID = 24

// What a layer fitting this image to its text has to know, derived here so a change to the
// patch layout reaches the fit math rather than silently disagreeing with it.
export const SHARED_CHIP_FIT = {
  // Fixed column left of the text: outline, padding and the glyph.
  leadPx: BORDER_PX + PAD_LEFT_PX + ICON_PX,
  // ...and the one right of it. Not the same number, which is why a layer centring the chip
  // under its pin has to read both.
  trailPx: PAD_RIGHT_PX + BORDER_PX,
  // The plate never grows vertically, so this is its height, not a minimum.
  fixedHeightPx: HEIGHT_PX,
  // The shadow reaches below the plate but not above it, so the image is taller than the
  // plate on one side only — which puts the plate off the image's centre, and the fit centres
  // the IMAGE on the text. The layer owes the quad half that difference back.
  shadowRecenterPx: MAP_SHADOW_REACH_PX.bottom / 2,
} as const

// One source for the whole 9-patch — [outline pad glyph] | band | [pad outline], each fixed end
// wide enough to hold its corner arcs so the radius survives the stretch. No vertical patch: at
// this height no row is both outside the arcs and outside the glyph, so width stretches alone.
function sharedChipGeometry(ratio: number) {
  const border = BORDER_PX * ratio
  const radius = RADIUS_PX * ratio
  const plateH = Math.round(HEIGHT_PX * ratio)
  const icon = Math.round(ICON_PX * ratio)
  const band = Math.max(1, Math.round(BAND_PX * ratio))

  // Sides and below only: mapbox takes the collision box from the whole image, and the two
  // pixels above the chip are all that keep its own pin from suppressing it. Cutting flat up
  // there is free — both layers cast downward, so the shadow above peaks under 3% black.
  const padX = Math.round(MAP_SHADOW_REACH_PX.side * ratio)
  const padBottom = Math.round(MAP_SHADOW_REACH_PX.bottom * ratio)
  const height = plateH + padBottom

  const leadInset = padX + Math.round(SHARED_CHIP_FIT.leadPx * ratio)
  const trailInset = padX + Math.round(SHARED_CHIP_FIT.trailPx * ratio)
  const lead = Math.max(leadInset, padX + Math.ceil(radius))
  const trail = Math.max(trailInset, padX + Math.ceil(radius))
  const width = lead + band + trail

  return {
    width,
    height,
    plateH,
    plateLeft: padX,
    plateRight: width - padX,
    border,
    radius,
    icon,
    iconLeft: padX + Math.round((BORDER_PX + PAD_LEFT_PX) * ratio),
    iconTop: Math.round((plateH - icon) / 2),
    metadata: {
      pixelRatio: ratio,
      stretchX: [[lead, lead + band]] as Array<[number, number]>,
      // Where the word goes. Measured from the edges rather than from the stretch band: the
      // trailing fixed slice is widened to cover its corner and the shadow, and the text must
      // not move with either. Full height, as the plate's own height is what centres the line.
      content: [lead, 0, width - trailInset, height] as [
        number,
        number,
        number,
        number,
      ],
    },
  }
}

// Registered on demand like every other GL image: one id, no state, so it renders once per map.
export function registerMissingSharedChipImage(
  map: MapboxMap,
  imageId: string,
): void {
  if (imageId !== SHARED_CHIP_IMAGE_ID || map.hasImage(imageId)) {
    return
  }

  const ratio = window.devicePixelRatio || 1
  const {
    width,
    height,
    plateH,
    plateLeft,
    plateRight,
    border,
    radius,
    icon,
    iconLeft,
    iconTop,
    metadata,
  } = sharedChipGeometry(ratio)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return
  }

  // Assembled off-screen first: the shadow belongs to the plate AND its glyph as one group,
  // and the art can only be composited once or its antialiased edges stack.
  const art = document.createElement('canvas')
  art.width = width
  art.height = height
  const artCtx = art.getContext('2d')
  if (!artCtx) {
    return
  }

  // Inset by half the stroke so the outline lands inside the plate rather than half outside
  // it, which also takes half a stroke off the radius it traces.
  traceRoundRect(
    artCtx,
    plateLeft + border / 2,
    border / 2,
    plateRight - plateLeft - border,
    plateH - border,
    Math.max(0, radius - border / 2),
  )
  artCtx.fillStyle = Colors.statusClosedSolid
  artCtx.fill()
  artCtx.lineWidth = border
  artCtx.strokeStyle = Colors.mapWhite
  artCtx.stroke()

  artCtx.save()
  artCtx.translate(iconLeft, iconTop)
  artCtx.scale(icon / ICON_GRID, icon / ICON_GRID)
  artCtx.strokeStyle = Colors.mapWhite
  artCtx.lineWidth = DEFAULT_GLYPH_STROKE_WIDTH
  artCtx.lineCap = 'round'
  artCtx.lineJoin = 'round'
  for (const path of iconNodesToPath2Ds(SHARED_ICON_NODES)) {
    artCtx.stroke(path)
  }
  artCtx.restore()

  // Shadow, then art. Each pass draws the finished art a full canvas height above the top
  // edge, where it is clipped away, and lets its own offset carry the shadow back down.
  MAP_SHADOW_LAYERS.forEach(({ offsetYPx, blurPx, color }) => {
    ctx.shadowColor = color
    ctx.shadowBlur = blurPx * ratio
    ctx.shadowOffsetY = offsetYPx * ratio + height
    ctx.drawImage(art, 0, -height)
  })
  ctx.shadowColor = 'transparent'
  ctx.shadowBlur = 0
  ctx.shadowOffsetY = 0
  ctx.drawImage(art, 0, 0)

  map.addImage(imageId, ctx.getImageData(0, 0, width, height), {
    pixelRatio: ratio,
    ...metadata,
  })
}
